import { AdminDropdownsBaseController } from './admin_dropdowns_base_controller';
import Rails from "@rails/ujs";

let debounce = require('lodash/debounce');

export default class extends AdminDropdownsBaseController {
  static targets = [
    "menu",
    "menuItem",
    "icon",
    "label",
    "promptOption",
    "component",
    "hiddenField",
    "multiselectHiddenFieldsContainer",
    "menuFilter",
    "clearFilterIcon",
    "presenceValidationError",
    "itemHint",
    "hint"
  ];

  static values = {
    direction: String,
    prompt: String,
    placement: String,
    multiselect: Boolean,
    selectedCopy: String
  }

  connect() {
    this.filterHandler = debounce(this.filterOnInput, 300).bind(this);
    this.validatePresenceHandler = this.validatePresence.bind(this);

    if (this.hasPresenceValidationErrorTarget) {
      document.addEventListener('submit', this.validatePresenceHandler)
    }

    if (this.hasMenuFilterTarget) {
      this.menuFilterTarget.addEventListener('input', this.filterHandler);
    }

    const preSelectedItem = this._preSelectedItem();

    if (preSelectedItem) {
      this._showPromptOption();
      this._replaceSelectedOption(preSelectedItem);
      this._toggleItemHint();
    }
  }

  disconnect() {
    document.removeEventListener('click', this.clickHandler);
    window.removeEventListener('resize', this.resizeHandler);

    if (this.hasPresenceValidationErrorTarget) {
      document.removeEventListener('submit', this.validatePresenceHandler)
    }

    if (this.hasMenuFilterTarget) {
      this.menuFilterTarget.removeEventListener('input', this.filterHandler);
    }
  }

  _afterToggleMenu() {
    this._focusInInput();
  }

  toggleOption(event) {
    let target = event.currentTarget;

    if (this.hasMenuFilterTarget) {
      this.filterReset();
    }

    if (this.multiselectValue) {
      this._toggleItem(target);
      this._updateMultiselectHiddenFields();
      this._replaceSelectedOption();
      this._toggleActiveClass();
      this._triggerChangeEvent();
      this._clearStateError();

      return;
    }

    this._deselectItems();
    this._selectItem(target);
    this._setHiddenFieldValue(target);
    this._showPromptOption();
    this.toggleMenu();
    this._triggerChangeEvent();
    this._replaceSelectedOption(target);
    this._hideErrorMessage();
    this._clearStateError();
    this._toggleItemHint();
    this._toggleActiveClass();
  }

  resetDropdown(event) {
    this._replaceSelectedOption(event.currentTarget, false);
    this._resetState();
    this.toggleMenu();
  }

  resetSelection() {
    if (this.hasPromptOptionTarget) {
      this.labelTarget.innerHTML = this.promptOptionTarget.textContent.trim();
    }

    this._resetState();
  }

  _resetState() {
    this._hidePromptOption();
    this._resetHiddenFieldValue();
    this._deselectItems();
    this._toggleItemHint();
    this._triggerChangeEvent();
    this._removeActiveClass();
  }

  loadData(event) {
    if (this.ajaxRequest) {
      this.ajaxRequest.abort(); // Abort previous ajax request.
    }

    Rails.ajax({
      url: event.params.url,
      type: event.params.method || "GET",
      dataType: "script",
      beforeSend: (xhr) => {
        this.ajaxRequest = xhr;
        return true;
      }
    })
  }

  hideMenu(event) {
    if (this.element.contains(event.target) === false &&
      !this.menuTarget.classList.contains('hidden')) {
      this.toggleMenu();
    }
  }

  _focusInInput() {
    if (this.hasMenuFilterTarget) {
      this.menuFilterTarget.focus();
    }
  }

  filterOnInput(event) {
    const filterValue = event.target.value.toLowerCase();

    this._toggleClearFilterIcon();

    this.menuItemTargets.forEach((item) => {

      const itemText = item.textContent.toLowerCase();

      if (itemText.includes(filterValue)) {
        this._hidePromptOption();
        item.classList.remove('hidden');
      } else {
        this._hidePromptOption();
        item.classList.add('hidden');
      }
    });
  }

  filterReset() {
    if (!this.multiselectValue && this._hasSelectedItems()) {
      this._showPromptOption();
    }

    this.menuFilterTarget.value = '';
    this._showItems(this.menuItemTargets);
    this._toggleClearFilterIcon();
  }

  _hasValidValue() {
    if (this.multiselectValue) {
      return this._hasSelectedItems();
    }
    return this.hiddenFieldTarget?.value?.trim();
  }

  _preSelectedItem() {
    let selectedItem;

    this.menuItemTargets.forEach((item) => {
      if (item.classList.contains("js-selected")) {
        selectedItem = item;
      }
    });

    return selectedItem;
  }

  _replaceSelectedOption(target = null, display_prompt = true) {
    if (this.multiselectValue) {
      this._replaceMultiSelectOption();
    } else if (display_prompt && this.promptValue) {
      const targetText = target.querySelector(".js-menu-option-text");
      targetText.classList.add("truncate")

      this.labelTarget.innerHTML = `${this.promptValue} ${targetText.innerHTML}`;
    } else {
      this.labelTarget.innerHTML = target.innerHTML;

      const labelMenuOptionText = this.labelTarget.querySelector(".js-menu-option-text");

      if (labelMenuOptionText) {
        labelMenuOptionText.classList.add("truncate");
      }
    }

    const menuOptionCheck = this.labelTarget.querySelector(".js-menu-option-check");

    if (display_prompt && menuOptionCheck) {
      menuOptionCheck.classList.add("invisible");
    }

    if (this.labelTarget.querySelector(".js-menu-option-text")) {
      this.labelTarget.querySelector(".js-menu-option-text").classList.remove("ml-5");
    }
  }

  _toggleItemHint() {
    if (this.hasItemHintTarget) {
      this.itemHintTargets.forEach(hint => hint.classList.add("hidden"));

      const selectedItem = this.menuItemTargets.find(
        item => item.classList.contains("js-selected")
      );

      if (selectedItem) {
        const itemHint = this.itemHintTargets.find(
          hint => hint.dataset.value === selectedItem.dataset.value
        );

        if (itemHint) {
          itemHint.classList.remove("hidden");
          this._toggleHint(true);
        } else {
          this._toggleHint(false);
        }
      } else {
        this._toggleHint(false);
      }
    }
  }

  _toggleHint(hidden) {
    if (this.hasHintTarget) {
      const hasContent = this.hintTarget.textContent.trim().length > 0;
      this.hintTarget.classList.toggle('hidden', hidden || !hasContent);
    }
  }

  _removeActiveClass() {
    this.componentTarget.firstElementChild.classList.remove("active");
  }

  _toggleClearFilterIcon() {
    this._toggleFilterIcon(this.clearFilterIconTarget, this.menuFilterTarget.value);
  }

  _showItems(items) {
    items.forEach((item) => {
      item.classList.remove('hidden');
    });
  }

  _hasSelectedItems() {
    return this.menuItemTargets.some((item) => item.classList.contains("js-selected"));
  }

  _deselectItems() {
    this.menuItemTargets.forEach((item) => {
      item.querySelector(".js-menu-option-check").classList.add("invisible");
      item.classList.remove("js-selected");
    });
  }

  _toggleItem(target) {
    target.querySelector(".js-menu-option-check").classList.toggle("invisible");
    target.classList.toggle("js-selected");
  }

  _selectItem(target) {
    target.querySelector(".js-menu-option-check").classList.remove("invisible");
    target.classList.add("js-selected");
  }

  _setHiddenFieldValue(target) {
    this.hiddenFieldTarget.value = target.dataset.value;
  }

  _resetHiddenFieldValue() {
    this.hiddenFieldTarget.value = '';
  }

  _updateMultiselectHiddenFields() {
    if (!this.hasMultiselectHiddenFieldsContainerTarget) return;

    const container = this.multiselectHiddenFieldsContainerTarget;
    const fieldName = this.hiddenFieldTarget.name;

    container.innerHTML = '';

    if (this._numberOfSelectedItems() === 0) {
      // We gotta create at least one empty hidden field, otherwise every check
      // that relies on this will throw an exception
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = fieldName;
      input.value = '';
      input.setAttribute('data-components--admin-dropdowns-dropdown-target', 'hiddenField');
      container.appendChild(input);
    } else {
      this.menuItemTargets.forEach((item) => {
        if (item.classList.contains("js-selected")) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = fieldName;
          input.value = item.dataset.value;
          input.setAttribute('data-components--admin-dropdowns-dropdown-target', 'hiddenField');
          container.appendChild(input);
        }
      });
    }
  }

  _replaceMultiSelectOption() {
    let selectedItemsCount = this._numberOfSelectedItems();
    let selectedItem = this.menuItemTargets
      .find(item => item.classList.contains("js-selected"));

    let selectedItemText = selectedItem?.innerHTML;

    this._updateMultiselectLabel(selectedItemsCount, selectedItemText);
  }

  _dropdownCoords() {
    return this.componentTarget.firstElementChild.getBoundingClientRect();
  }

  _clearStateError() {
    this.element.classList.remove('error', 'warning', 'success');
    this._toggleHint(false);
  }

  _numberOfSelectedItems() {
    let counter = 0;

    this.menuItemTargets.forEach((item) => {
      if (item.classList.contains("js-selected")) {
        counter++;
      }
    });

    return counter;
  }
}
